import { UserOutlined, CalendarOutlined, DeleteOutlined, EditOutlined, WhatsAppOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { Button, Space, Popover, Popconfirm, Tag, Modal, Input } from 'antd';
import axios from 'axios';
import { format } from 'date-fns';
import config from '../../../config';
import LivraisonViewPrix from './LivraisonViewPrix';
import { useSelector } from 'react-redux';
import DetailPointure from '../../ventes/detailPointureVente/DetailPointure';
import PageDetails from '../../PageDetails/PageDetails';
import canSeeFinance from '../../../utils/canSeeFinance';
import {
  NdoeToolbar, NdoeActionButton, NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge, NdoeImage,
} from '../../../components/ndoe';

const LivraisonView = ({ id }) => {
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [open, setOpen] = useState(false);
    const [idClient, setIdClient] = useState({});
    const [prixCommandeId, setPrixCommandeId] = useState(null);
    const [prix, setPrix] = useState({});
    const [searchValue, setSearchValue] = useState('');
    const [getCommande, setGetCommande] = useState([]);
    const user = useSelector((state) => state.user?.currentUser);
    const isAdmin = user?.role === 'admin';
    const showFinance = canSeeFinance(user);
    // Le PRIX DE VENTE d'une livraison est opérationnel (négocié à la livraison) :
    // la secrétaire, que le back autorise déjà à le modifier (routes livraison =
    // admin+secrétaire), doit pouvoir le voir et l'éditer. Distinct de la finance
    // confidentielle (coûts d'achat / marges / caisse) qui reste admin (canSeeFinance).
    const canPrix = user?.role === 'admin' || user?.role === 'secretaire';
    const [pointure, setPointure] = useState('');
    const [idVariant, setvariant] = useState({});
    const [openVariant, setOpenVariant] = useState('');
    const [openPointure, setOpenPointure] = useState('');

    const fetchDetail = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/livraison/livraisonDetailOne/${id}`);
        setData(data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    const handleDelete = async (idDetail) => {
      try {
        await axios.delete(`${DOMAIN}/api/livraison/livraisonDeleteDetail/${idDetail}`);
        fetchDetail();   // refetch au lieu de recharger toute la page
      } catch (err) {
        console.log(err);
      }
    };

    const showModals = (e, p) => { setOpenPointure(true); setvariant(e); setPointure(p); };
    const showModalPhone = (e) => { setOpenVariant(true); setvariant(e); };
    // édition prix : id_detail_livraison + id_commande (pour le bouton « puis voir la commande »)
    const showModal = (idDetailLivraison, idCommande) => { setOpen(true); setIdClient(idDetailLivraison); setPrixCommandeId(idCommande || null); };

    const baseColumns = [
      { title: '#', dataIndex: 'id', key: 'id', render: (t, r, index) => index + 1, width: "3%" },
      {
        title: 'image', dataIndex: 'img', key: 'img',
        render: (text, record) => (
          <NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />
        ),
      },
      {
        title: 'Marque', dataIndex: 'nom_marque', key: 'nom_marque',
        render: (text, record) => (
          <Popover title="Voir la fiche de ce produit" trigger="hover">
            <Tag color="blue" onClick={() => showModalPhone(record.id_varianteProduit)} style={{ cursor: 'pointer' }}>{text}</Tag>
          </Popover>
        ),
      },
      {
        title: 'Pointure', dataIndex: 'pointure', key: 'pointure',
        sorter: (a, b) => a.pointure.length - b.pointure.length,
        render: (text, record) => (
          <Popover title={`Historique de la pointure ${record.taille}`} trigger="hover">
            <Tag color="blue" onClick={() => showModals(record.id_varianteProduit, record.id_taille)} style={{ cursor: 'pointer' }}>{text}</Tag>
          </Popover>
        ),
      },
      {
        title: 'Client', dataIndex: 'nom_client', key: 'nom_client',
        render: (text) => <Tag color="green"><UserOutlined style={{ marginRight: 5 }} />{text}</Tag>,
      },
      {
        title: 'Livreur', dataIndex: 'nom_livreur', key: 'nom_livreur',
        render: (text) => <Tag color="blue"><UserOutlined style={{ marginRight: 5 }} />{text}</Tag>,
      },
      {
        title: 'Qté livrée', dataIndex: 'qte_livre', key: 'qte_livre',
        sorter: (a, b) => a.qte_livre - b.qte_livre, sortDirections: ['descend', 'ascend'],
        render: (text) => <Tag color="blue">{text}</Tag>,
      },
      {
        title: 'Qté en livraison', dataIndex: 'qte_commande', key: 'qte_commande',
        sorter: (a, b) => a.qte_commande - b.qte_commande, sortDirections: ['descend', 'ascend'],
        render: (text) => <Tag color="green">{text}</Tag>,
      },
      {
        /*
          LE RÉSULTAT DE LA PAIRE — « ce qui a été vendu ou pas ».

          Demande de la propriétaire (21/08/2026). La donnée était DÉJÀ servie :
          la requête fait `SELECT detail_livraison.*`, donc `statut_livraison`
          arrivait dans chaque ligne — l'écran ne l'affichait simplement pas. On
          lisait le prix sans savoir s'il avait été encaissé.

          Placé AVANT le prix : la première question est « vendue ou rendue ? »,
          le montant ne se lit qu’ensuite.
        */
        title: 'Résultat', dataIndex: 'statut_livraison', key: 'statut_livraison', width: 150,
        render: (t) => (t
          ? <NdoeStatusBadge status={t} />
          : <span style={{ color: '#a8b0bd' }}>En cours</span>),
      },
      {
        title: 'Prix', dataIndex: 'prix', key: 'prix', price: true,
        sorter: (a, b) => a.prix - b.prix, sortDirections: ['descend', 'ascend'],
        render: (text, record) => (
          <Tag color="green" onClick={() => showModal(record.id_detail_livraison, record.id_commande)} style={{ cursor: 'pointer' }}>
            {parseFloat(text).toLocaleString('fr-FR', { style: 'currency', currency: 'USD' })}
          </Tag>
        ),
      },
      {
        title: 'Date création', dataIndex: 'date_creation', key: 'date_creation',
        render: (text) => <Tag color="blue" icon={<CalendarOutlined />}>{format(new Date(text), 'dd-MM-yyyy')}</Tag>,
      },
      {
        title: 'Modifié par', dataIndex: 'modifier', key: 'modifier',
        render: (text) => (
          <Tag color={text === null ? 'green' : 'blue'}>
            {text !== null ? <UserOutlined style={{ marginRight: 5 }} /> : null}
            {text !== null ? text : 'Non modifié'}
          </Tag>
        ),
      },
      {
        title: 'Action', key: 'action',
        render: (text, record) => (
          <Space size="middle">
            {isAdmin &&
            <Popover title="Supprimer" trigger="hover">
              <Popconfirm title="Êtes-vous sûr de vouloir supprimer?" onConfirm={() => handleDelete(record.id_detail_livraison)} okText="Oui" cancelText="Non">
                <Button icon={<DeleteOutlined />} style={{ color: 'red' }} />
              </Popconfirm>
            </Popover>}
          </Space>
        ),
      },
    ];

    // finance (coûts/marges) → admin ; price (prix de vente livraison) → admin+secrétaire.
    const columns = baseColumns.filter((c) => (!c.finance || showFinance) && (!c.price || canPrix));

    useEffect(() => {
      fetchDetail();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DOMAIN, id]);

    useEffect(() => {
      const fetchPrix = async () => {
        try {
          const { data } = await axios.get(`${DOMAIN}/api/livraison/livraisonPrix/${idClient}`);
          setPrix(data[0].prix);
        } catch (error) {
          console.log(error);
        }
      };
      fetchPrix();
    }, [DOMAIN, idClient]);

    useEffect(() => {
      const fetchCommande = async () => {
        try {
          const { data } = await axios.get(`${DOMAIN}/api/commande/commandeOne/${id}`);
          setGetCommande(data[0]);
        } catch (error) {
          console.log(error);
        }
      };
      fetchCommande();
    }, [DOMAIN, id]);

    const filteredData = data?.filter((item) =>
      item.nom_client?.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.nom_marque?.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.nom_livreur?.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.pointure?.toLowerCase().includes(searchValue.toLowerCase())
    );

    const phone = getCommande?.telephone;

    const renderCard = (record) => {
      const metrics = [
        { value: record.qte_livre, label: 'livrée' },
        { value: record.qte_commande, label: 'commandée' },
      ];
      if (canPrix) metrics.push({ value: parseFloat(record.prix).toLocaleString('fr-FR', { style: 'currency', currency: 'USD' }), label: 'prix' });
      const actions = [];
      if (canPrix) actions.push(
        <NdoeActionButton key="p" size="sm" variant="secondary" icon={<EditOutlined />} onClick={() => showModal(record.id_detail_livraison, record.id_commande)}>Prix</NdoeActionButton>
      );
      if (isAdmin) actions.push(
        <Popconfirm key="d" title="Supprimer cette ligne ?" onConfirm={() => handleDelete(record.id_detail_livraison)} okText="Oui" cancelText="Non">
          <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />}>Suppr.</NdoeActionButton>
        </Popconfirm>
      );
      return (
        <NdoeDataCard
          media={<NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />}
          title={record.nom_marque}
          subtitle={`P.${record.pointure} · ${record.nom_client ?? ''}`}
          /* Sur téléphone, le badge portait « Prix modifié » — utile, mais la
             question première est le RÉSULTAT. On montre le résultat, et le prix
             modifié passe en mention sous le titre (voir subtitle). */
          badge={record.statut_livraison
            ? <NdoeStatusBadge status={record.statut_livraison} />
            : (record.modifier ? <NdoeStatusBadge kind="custom" tone="info" label="Prix modifié" /> : null)}
          metrics={metrics}
          onClick={() => showModalPhone(record.id_varianteProduit)}
          actions={actions}
        />
      );
    };

  return (
    <div>
      <div style={{ marginBottom: 14, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div className="ndoe-eyebrow">Logistique</div>
          <h2 className="ndoe-page-title" style={{ fontSize: 20 }}>Détail de la livraison N° {id}</h2>
          <p className="ndoe-page-subtitle">
            {getCommande?.nom ? `${getCommande.nom} — ` : ''}Commune {getCommande?.nom_commune ?? '—'} · Av/ {getCommande?.avenue ?? ''} Q/ {getCommande?.quartier ?? ''} N° {getCommande?.num ?? ''}
          </p>
        </div>
        {phone && (
          <NdoeActionButton variant="secondary" icon={<WhatsAppOutlined />} as="a" href={`https://wa.me/${String(phone).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer">
            {phone}
          </NdoeActionButton>
        )}
      </div>

      <NdoeToolbar
        search={
          <Input.Search
            type="search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Rechercher (client, marque, livreur, pointure)…"
            allowClear
          />
        }
      />

      <NdoeResponsiveTable
        data={filteredData}
        columns={columns}
        rowKey="id_detail_livraison"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10 }}
        renderCard={renderCard}
        empty={{ icon: '📦', title: 'Aucune ligne', text: 'Cette livraison est vide.' }}
        stickyHeader={false} /* ⑩ rendu en modale : pas d'en-tête collant (offset topbar hors contexte). */
      />

      <Modal title="Modifier le prix" centered open={open} onCancel={() => setOpen(false)} width={420} footer={[]} destroyOnClose>
        <LivraisonViewPrix
          prixTotal={prix}
          idDetail={idClient}
          userUpdate={user.id}
          idCommande={prixCommandeId}
          onSaved={() => { setOpen(false); fetchDetail(); }}
        />
      </Modal>

      <Modal title="" centered open={openVariant} onCancel={() => setOpenVariant(false)} width={1000} footer={[]}>
        <PageDetails id={idVariant} />
      </Modal>

      <Modal title="" centered open={openPointure} onCancel={() => setOpenPointure(false)} width={1100} footer={[]}>
        <DetailPointure idVariant={idVariant} idTaille={pointure} />
      </Modal>
    </div>
  );
}

export default LivraisonView
